import { Routes } from '@angular/router';

import { AuthGuard } from '@auth0/auth0-angular';

import { AssetGuard } from '../features/asset/service/asset.guard';
import { viewPageNoMetaResolveFn } from '../features/deal/services/view-entity.resolver';
import { ActivityEvent } from '../features/home/models/activity-event.model';
import { SemerisRoutes } from '../features/shared/constants/routes';
import {
  Permission,
  PermissionRelation,
  PermissionsWithRelation,
} from '../features/shared/models/api/permissions.enum';
import { EmbeddedGuard } from '../features/shared/services/embedded.guard';
import { PermissionGuard } from '../features/shared/services/permission.guard';
import { WelcomeGuard } from '../features/shared/services/welcome.guard';

export const appRoutes: Routes = [
  {
    path: SemerisRoutes.home.root,
    loadChildren: () => import('../features/home/routes').then((m) => m.homeRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.admin.root,
    loadChildren: () => import('../features/admin/routes').then((m) => m.adminRoutes),
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.asset.changeAsset,
    loadChildren: () => import('../features/asset/routes').then((m) => m.assetRoutes),
    canActivate: [AuthGuard],
  },
  {
    path: SemerisRoutes.auth.root,
    loadChildren: () => import('../features/authentication-ui/routes').then((m) => m.authenticationUiRoutes),
    canActivate: [],
  },
  {
    path: SemerisRoutes.integrations.root,
    loadChildren: () => import('../features/integration/routes').then((m) => m.integrationRoutes),
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.upgrade.root,
    loadChildren: () => import('../features/upgrade/routes').then((m) => m.upgradeRoutes),
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.analysisAccelerator.root,
    loadChildren: () => import('../features/analysis-accelerator/routes').then((m) => m.analysisAcceleratorRoutes),
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.settings.root,
    loadChildren: () => import('../features/settings/routes').then((m) => m.settingsRoutes),
    canActivate: [AuthGuard],
  },
  {
    path: 'benchmark/json-editor',
    loadComponent: () =>
      import(
        '../features/json-editor/containers/json-editor-benchmark/json-editor-benchmark.component'
      ).then((m) => m.JsonEditorBenchmarkComponent),
    canActivate: [AuthGuard],
  },
  {
    path: SemerisRoutes.unauthorized,
    loadComponent: () =>
      import('../features/unauthorized/containers/unauthorized/unauthorized.component').then(
        (m) => m.UnauthorizedComponent
      ),
    canActivate: [AuthGuard],
  },
  {
    path: SemerisRoutes.welcome,
    loadComponent: () =>
      import('../features/shared/components/welcome/welcome.component').then((m) => m.WelcomeComponent),
    canActivate: [AuthGuard, WelcomeGuard],
  },
  {
    path: SemerisRoutes.blocked,
    loadComponent: () =>
      import('../features/shared/components/blocked-landing/blocked-landing.component').then(
        (m) => m.BlockedLandingComponent
      ),
  },
  {
    path: SemerisRoutes.formula.root,
    loadChildren: () => import('../features/formula/routes').then((m) => m.formulaRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.datapoint.root,
    loadChildren: () => import('../features/unified-datapoint/routes').then((m) => m.unifiedDatapointRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: `${SemerisRoutes.transactions.root}/${SemerisRoutes.transactions.getByParametersEmbedded}`,
    loadChildren: () => import('../features/embedded/routes').then((m) => m.embeddedRoutes),
    canActivate: [EmbeddedGuard, AssetGuard],
  },
  {
    path: SemerisRoutes.documents.root,
    loadChildren: () => import('../features/document/routes').then((m) => m.documentRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.search.documentSearch,
    loadChildren: () => import('../features/document-search/routes').then((m) => m.documentSearchRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },

  {
    path: SemerisRoutes.managers.root,
    loadChildren: () => import('../features/manager/routes').then((m) => m.managerRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.transactions.root,
    loadChildren: () => import('../features/deal/routes').then((m) => m.dealRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.dealFilter.root,
    loadChildren: () => import('../features/deal-filter/routes').then((m) => m.dealFilterRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.marketComparison.root,
    loadChildren: () => import('../features/market-comparison/routes').then((m) => m.marketComparisonRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.identifiers.root,
    loadChildren: () => import('../features/identifier/routes').then((m) => m.identifierRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.topic.root,
    loadChildren: () => import('../features/topic/routes').then((m) => m.topicRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.checklist.root,
    loadChildren: () => import('../features/checklist/routes').then((m) => m.checklistRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.labels,
    loadComponent: () =>
      import('../features/label/containers/label-list-container/label-list-container.component').then(
        (m) => m.PermissionListContainerComponent
      ),
    data: {
      permissions: [Permission.READ_LABEL],
      permissionsRelation: PermissionRelation.AND,
    } as PermissionsWithRelation,
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.billing,
    loadComponent: () =>
      import('../features/billing/containers/billing-list-container/billing-list-container.component').then(
        (m) => m.BillingListContainerComponent
      ),
    data: {
      permissions: [Permission.READ_BILLING, Permission.READ_BILLING_ADMIN],
      permissionsRelation: PermissionRelation.OR,
    } as PermissionsWithRelation,
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.publication,
    loadComponent: () =>
      import('../features/publication/containers/publication-list-container/publication-list-container.component').then(
        (m) => m.PublicationListContainerComponent
      ),
    data: {
      permissions: [Permission.READ_PUBLICATION],
      permissionsRelation: PermissionRelation.OR,
    } as PermissionsWithRelation,
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.newsFeed,
    loadComponent: () =>
      import('../features/news-feed/containers/news-feed-container/news-feed-container.component').then(
        (m) => m.NewsFeedContainerComponent
      ),
    resolve: { activity: viewPageNoMetaResolveFn },
    data: {
      permissions: [Permission.READ_NEWS_CRAWL],
      permissionsRelation: PermissionRelation.AND,
      activityEventType: ActivityEvent.NewsScannerView,
    } as PermissionsWithRelation,
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: `${SemerisRoutes.conversations.root}/${SemerisRoutes.conversations.helpAi}`,
    loadComponent: () =>
      import('../features/comment/containers/conversation-container/conversation-container.component').then(
        (m) => m.ConversationContainerComponent
      ),
    data: {
      permissions: [Permission.WRITE_HELP_AI_ADMIN],
      permissionsRelation: PermissionRelation.AND,
    } as PermissionsWithRelation,
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.notes,
    loadComponent: () =>
      import('../features/comment/containers/conversation-list-container/conversation-list-container.component').then(
        (m) => m.ConversationListContainerComponent
      ),
    data: {
      permissions: [Permission.READ_COMMENT],
      permissionsRelation: PermissionRelation.AND,
    } as PermissionsWithRelation,
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.tickler,
    loadComponent: () =>
      import('../features/timeline/containers/tickler-container/tickler-container.component').then(
        (m) => m.TicklerContainerComponent
      ),
    data: {
      permissions: [Permission.READ_TICKLER],
      permissionsRelation: PermissionRelation.AND,
    } as PermissionsWithRelation,
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.datapointOrganizer.root,
    loadComponent: () =>
      import(
        '../features/datapoint-organizer/containers/datapoint-organizer-container/datapoint-organizer-container.component'
      ).then((m) => m.DatapointOrganizerContainerComponent),
    data: {
      permissions: [Permission.READ_DATAPOINT_ORGANIZER],
      permissionsRelation: PermissionRelation.AND,
    } as PermissionsWithRelation,
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.termSynonymOrganizer.root,
    loadComponent: () =>
      import(
        '../features/term-synonym-organizer/containers/term-synonym-organizer-container/term-synonym-organizer-container.component'
      ).then((m) => m.TermSynonymOrganizerContainerComponent),
    data: {
      permissions: [Permission.READ_TERM_SYNONYM_ORGANIZER],
      permissionsRelation: PermissionRelation.AND,
    } as PermissionsWithRelation,
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.positionList.root,
    loadChildren: () => import('../features/position-list/routes').then((m) => m.positionListRoutes),
  },
  {
    path: SemerisRoutes.help.root,
    children: [
      {
        path: '**',
        loadComponent: () =>
          import('../features/help/containers/help-page/help-page.component').then((m) => m.HelpPageComponent),
      },
    ],
    canActivate: [AuthGuard],
  },
  {
    path: SemerisRoutes.analystCorner.root,
    children: [
      {
        path: SemerisRoutes.analystCorner.create,
        loadComponent: () =>
          import('../features/analyst-corner/containers/analyst-corner-create/analyst-corner-create.component').then(
            (m) => m.AnalystCornerCreateComponent
          ),
      },
      {
        path: `${SemerisRoutes.analystCorner.edit.root}/${SemerisRoutes.analystCorner.edit.byId}`,
        loadComponent: () =>
          import('../features/analyst-corner/containers/analyst-corner-create/analyst-corner-create.component').then(
            (m) => m.AnalystCornerCreateComponent
          ),
      },
      {
        path: SemerisRoutes.analystCorner.byId,
        loadComponent: () =>
          import(
            '../features/analyst-corner/containers/analyst-corner-container/analyst-corner-container.component'
          ).then((m) => m.AnalystCornerContainerComponent),
      },
      {
        path: '',
        loadComponent: () =>
          import(
            '../features/analyst-corner/containers/analyst-corner-container/analyst-corner-container.component'
          ).then((m) => m.AnalystCornerContainerComponent),
      },
    ],
    data: {
      permissions: [Permission.READ_ANALYST_CORNER],
      permissionsRelation: PermissionRelation.AND,
    } as PermissionsWithRelation,
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: '**',
    loadComponent: () =>
      import('../features/shared/components/not-found/not-found.component').then((m) => m.NotFoundComponent),
    canActivate: [AuthGuard],
  },
];
